import { Row } from 'antd';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import useDeclarationFooter from 'hooks/useDeclarationFooter';
import useDeclarationValidation from 'hooks/useDeclarationValidation';
import { DeclarationFooterType } from 'store/declaration-footer/declaration-footer';
import { FormAction } from '../../../utils/form-utils';
import { Events } from 'utils/events.enum';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import { PaginatedParams } from 'core/http/pagination';
import useDeclarations from 'hooks/useDeclarations';
import useProducts from 'hooks/useProducts';
import { getDeclarationUrl } from '../../../utils/getDeclarationUrl';
import { BtMasterGoodsNotSelected, BtMasterGoodsSelected, FormTabsWrapper, InvalidIcon } from '../Form.styles';
import { FormTabs } from '../DeclarationFormTab';
import ProductsNavigation, { ProductsNavigationTypes } from './ProductsNavigation';
import { ProductsTypes } from 'store/products/reducer';
import { ICS2ConsignmentHouseLevel, ICS2GoodsItemResponse } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { ListPayload } from 'core/http/response';
import DeclarationInformation from 'views/declarations/common/DeclarationInformation';
import { InfoWrapper } from 'views/declarations/common/DeclarationInformation.styles';
import Box44 from 'views/declarations/common/box44/Box44';
import useOpenBox44OnRequest from 'views/declarations/common/box44/useOpenBox44OnRequest';
import { hasIcs2MasterItems } from 'views/declarations/eu/ics2/utils';
import getProductItemNumber from 'views/declarations/common/getProductItemNumber';

type TabConfig = {
    label: string;
    route: string;
    error?: boolean;
    footerType?: DeclarationFooterType;
};

interface Props {
    products: ListPayload<ProductsTypes>;
    houseConsignments: ICS2ConsignmentHouseLevel[];
    houseGoodsItems?: ICS2GoodsItemResponse[];
    selectedFormTab: FormTabs;
    numItems: number;
    viewOnly?: boolean;
    amendment?: boolean;
    showBulkUploadItems?: boolean;
    setNewProduct?: (value: boolean) => void;
    handleCreateDeclarationProduct?: () => Promise<void>;
    handleCreateMasterItemProduct?: () => Promise<void>;
    handleCreateHouseConsignment?: () => Promise<void>;
    handleCreateHouseConsignmentProduct?: (houseConsignmentId: string) => Promise<void>;
    setShowProducts?: (value: boolean) => void;
    refetchProducts?: () => Promise<void>;
    fetchProductPage?: (params: Partial<PaginatedParams>) => Promise<ListPayload<ProductsTypes> | undefined>;
    activeProductParams?: Partial<PaginatedParams>;
    // Resolves to false when the open item could not be saved - the user then has to stay on it.
    saveOpenProductIfNeeded?: () => Promise<boolean>;
}

export const FormNavigation: FC<Props> = ({
    products,
    houseConsignments,
    houseGoodsItems,
    selectedFormTab,
    numItems,
    viewOnly,
    amendment,
    setNewProduct,
    handleCreateDeclarationProduct,
    showBulkUploadItems,
    handleCreateMasterItemProduct,
    handleCreateHouseConsignment,
    handleCreateHouseConsignmentProduct,
    setShowProducts,
    refetchProducts,
    fetchProductPage,
    activeProductParams,
    saveOpenProductIfNeeded,
}) => {
    const { dirty: isFormDirty, touched: formTouched } = useFormikContext();
    const hasUserChanges = isFormDirty && !isEmpty(formTouched);
    const { setDeclarationFooterType } = useDeclarationFooter();
    const { declaration } = useDeclarations();
    const { isLoading: isProductLoading } = useProducts();
    const {
        setFormAction,
        setHasUnsavedChanges,
        declarationValidation: { hasUnsavedChanges },
    } = useDeclarationValidation({
        formAction: null,
    });
    const navigate = useNavigate();
    const { declarationId, manifestId, jobId, productId, consignmentId } = useParams();
    const { declarationErrors: formDeclarationErrors } = useDeclarationFormErrors();
    const location = useLocation();

    const [showBox44Modal, setShowBox44Modal] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const [unfilteredTotal, setUnfilteredTotal] = useState(numItems);

    const basePath = getDeclarationUrl({ manifestId, jobId, declarationId });
    const isICS2ENS = !!declaration?.ics2ENSDeclaration;
    const hasMasterItems = hasIcs2MasterItems(declaration);
    const houseConsignmentsCount = houseConsignments.length;
    const currentHouseConsignmentIndex = houseConsignments.findIndex(({ id }) => id === consignmentId);
    const currentHouseGoodsItems = useMemo(
        () => (houseGoodsItems ?? []).filter((item) => item.houseConsignmentId === consignmentId),
        [consignmentId, houseGoodsItems]
    );
    const currentHouseGoodsItemIndex = currentHouseGoodsItems.findIndex(({ id }) => id === productId);
    const isPbn = !!declaration?.preBoardingNotification;
    const isGvms = !!declaration?.gvmsDeclaration;
    const currentItemIndex = products.list.findIndex((prod: any) => prod.id === productId);
    const globalIndex = currentItemIndex !== -1 ? products.pageNumber * products.pageSize + currentItemIndex : -1;
    // The header shows the number the item has in the table's `#` column. The row position changes
    // with the sort, so it is only the fallback - and the only option for ICS2, which numbers its
    // items on levels of their own.
    const rowPosition = globalIndex >= 0 && !isProductLoading ? globalIndex + 1 : undefined;
    const currentProductNumber = isICS2ENS ? undefined : getProductItemNumber(products.list[currentItemIndex]);
    const productNumber = rowPosition === undefined ? undefined : currentProductNumber ?? rowPosition;
    const canSave = hasUnsavedChanges && (!viewOnly || (viewOnly && amendment));

    /**
     * Persisting mid-amendment would overwrite the stored filing the backend compares the IE3Axx
     * against. Goods items are exempt: they reach the amendment only by being saved, and no
     * non-amendable element is editable at item level.
     */
    const blockDeclarationSave = viewOnly && amendment && isICS2ENS;

    const hasMasterDetailsError = useMemo(
        () => !isEmpty(formDeclarationErrors.masterDetails),
        [formDeclarationErrors.masterDetails]
    );
    const hasProductsError = useMemo(() => !isEmpty(formDeclarationErrors.items), [formDeclarationErrors.items]);
    const hasMasterItemsError = useMemo(
        () => !isEmpty(formDeclarationErrors.masterItems),
        [formDeclarationErrors.masterItems]
    );
    const hasHouseConsignmentsError = useMemo(
        () => !isEmpty(formDeclarationErrors.houseConsignments),
        [formDeclarationErrors.houseConsignments]
    );

    const tabConfig: Record<FormTabs, TabConfig> = useMemo(() => {
        return {
            [FormTabs.MASTER_DETAILS]: {
                label: 'Master Information',
                route: `${basePath}${viewOnly ? '/view-only' : ''}`,
                error: hasMasterDetailsError,
                footerType: DeclarationFooterType.MASTER_DETAILS,
            },
            [FormTabs.PRODUCTS]: {
                label: 'Items',
                route: `${basePath}${viewOnly ? '/view-only/products' : '/products'}`,
                error: hasProductsError,
                footerType: DeclarationFooterType.PRODUCTS,
            },
            [FormTabs.MASTER_ITEMS]: {
                label: 'Master Items',
                route: `${basePath}${viewOnly ? '/view-only/master-items' : '/master-items'}`,
                error: hasMasterItemsError,
            },
            [FormTabs.HOUSE_CONSIGNMENTS]: {
                label: 'House Consignment',
                route: `${basePath}${viewOnly ? '/view-only/house-consignments' : '/house-consignments'}`,
                error: hasHouseConsignmentsError,
            },
        };
    }, [basePath, hasMasterDetailsError, hasProductsError, hasMasterItemsError, hasHouseConsignmentsError, viewOnly]);

    const visibleTabs: FormTabs[] = useMemo(() => {
        const tabs: FormTabs[] = [FormTabs.MASTER_DETAILS];

        if (isICS2ENS) {
            if (hasMasterItems) tabs.push(FormTabs.MASTER_ITEMS);
            tabs.push(FormTabs.HOUSE_CONSIGNMENTS);
        }

        if (!(isPbn || isICS2ENS || isGvms)) {
            tabs.push(FormTabs.PRODUCTS);
        }

        return tabs;
    }, [isICS2ENS, isPbn, isGvms, hasMasterItems]);

    const hasAnyTabError = visibleTabs.some((tab) => tabConfig[tab].error);

    const goToTab = useCallback(
        (tab: FormTabs) => {
            setHasUnsavedChanges(false);
            setNewProduct?.(false);
            if (tabConfig[tab].footerType) setDeclarationFooterType(tabConfig[tab].footerType as DeclarationFooterType);
            navigate(tabConfig[tab].route, { replace: true });
        },
        [navigate, setDeclarationFooterType, setHasUnsavedChanges, setNewProduct, tabConfig]
    );

    // Saves the open item and resolves to whether the user can move on: a save that failed keeps
    // them on the item, where the error was reported, so they can fix it.
    const saveOpenProduct = useCallback(
        async () => (await saveOpenProductIfNeeded?.()) ?? true,
        [saveOpenProductIfNeeded]
    );

    const saveChangesAndMove = useCallback(
        async (targetTab: FormTabs) => {
            const productSaved = await saveOpenProduct();
            if (!productSaved) return;

            if (canSave) {
                switch (selectedFormTab) {
                    case FormTabs.MASTER_DETAILS:
                        if (!blockDeclarationSave) setFormAction(FormAction.DRAFT);
                        break;
                    // The house consignment form is part of the declaration, so it saves as a draft.
                    case FormTabs.HOUSE_CONSIGNMENTS:
                        if (!blockDeclarationSave) setFormAction(FormAction.DRAFT);
                        break;
                    default:
                        break;
                }
            }

            goToTab(targetTab);
        },
        [blockDeclarationSave, canSave, goToTab, saveOpenProduct, selectedFormTab, setFormAction]
    );

    const buildProductUrl = useCallback(
        (targetProductId: string) => {
            const segments = location.pathname.split('/').filter(Boolean);
            const lastIndex = segments.length - 1;
            if (segments[lastIndex] === productId) {
                segments[lastIndex] = targetProductId;
                return `/${segments.join('/')}`;
            }
            return null;
        },
        [location.pathname, productId]
    );

    /**
     * The save has to be finished before the url moves on. A house consignment is saved with the
     * declaration and the merge picks the entry to write into by the consignment id in the url, so
     * navigating first would fold these values into the consignment being opened; an item save
     * reads the item form values when it runs, which the route change resets out from under it.
     */
    const saveAndWait = useCallback(
        (action: FormAction) =>
            new Promise<void>((resolve) => {
                const subscriberId = window.EventBus.subscribe(Events.SAVING_DECLARATION_END, () => {
                    window.EventBus.unsubscribe(subscriberId);
                    resolve();
                });

                setFormAction(action);
            }),
        [setFormAction]
    );

    const handleBackToHouseConsignments = useCallback(async () => {
        setIsNavigating(true);
        try {
            if (productId) {
                const productSaved = await saveOpenProduct();
                if (!productSaved) return;
            } else if (hasUserChanges && canSave) {
                await saveAndWait(FormAction.DRAFT);
            }

            navigate(tabConfig[FormTabs.HOUSE_CONSIGNMENTS].route, { replace: true });
        } finally {
            setIsNavigating(false);
        }
    }, [canSave, hasUserChanges, navigate, productId, saveAndWait, saveOpenProduct, tabConfig]);

    const handleBackToMasterItems = useCallback(async () => {
        setIsNavigating(true);
        try {
            const productSaved = await saveOpenProduct();
            if (!productSaved) return;

            navigate(tabConfig[FormTabs.MASTER_ITEMS].route, { replace: true });
        } finally {
            setIsNavigating(false);
        }
    }, [navigate, saveOpenProduct, tabConfig]);

    // House consignments are all in the declaration payload, so stepping between them is a matter of
    // swapping the id in the url - there is no page to fetch the way there is for goods items.
    const handleNavigateToHouseConsignment = useCallback(
        async (direction: 'prev' | 'next') => {
            if (currentHouseConsignmentIndex === -1) return;

            const target = houseConsignments[currentHouseConsignmentIndex + (direction === 'next' ? 1 : -1)];
            if (!target?.id) return;

            const segments = location.pathname.split('/').filter(Boolean);
            const lastIndex = segments.length - 1;
            if (segments[lastIndex] !== consignmentId) return;

            setIsNavigating(true);
            try {
                if (hasUserChanges && canSave) await saveAndWait(FormAction.DRAFT);

                segments[lastIndex] = target.id;
                navigate(`/${segments.join('/')}`, { replace: true });
            } finally {
                setIsNavigating(false);
            }
        },
        [
            canSave,
            consignmentId,
            currentHouseConsignmentIndex,
            hasUserChanges,
            houseConsignments,
            location.pathname,
            navigate,
            saveAndWait,
        ]
    );

    const handleNavigateToProduct = useCallback(
        async (direction: 'prev' | 'next') => {
            if (!productId || currentItemIndex === -1) return;

            setIsNavigating(true);
            try {
                const productSaved = await saveOpenProduct();
                if (!productSaved) return;

                const targetIndexInPage = direction === 'next' ? currentItemIndex + 1 : currentItemIndex - 1;
                const targetItem = products.list[targetIndexInPage];

                if (targetItem?.id) {
                    const url = buildProductUrl(targetItem.id);
                    if (url) navigate(url, { replace: true });
                    return;
                }

                // Page boundary: fetch the adjacent page then navigate to its first/last item
                const targetPage = direction === 'next' ? products.pageNumber + 1 : products.pageNumber - 1;
                const result = await fetchProductPage?.({ ...activeProductParams, page: targetPage });
                if (!result?.list?.length) return;

                const newItem = direction === 'next' ? result.list[0] : result.list[result.list.length - 1];
                if (!newItem?.id) return;
                const url = buildProductUrl(newItem.id);
                if (url) navigate(url, { replace: true });
            } finally {
                setIsNavigating(false);
            }
        },
        [
            productId,
            currentItemIndex,
            saveOpenProduct,
            products.list,
            products.pageNumber,
            buildProductUrl,
            navigate,
            fetchProductPage,
            activeProductParams,
        ]
    );

    // Stepping between the items of a house consignment: they are all loaded, so it is the same
    // swap of the last url segment the goods items do, without the page boundary handling.
    const handleNavigateToHouseGoodsItem = useCallback(
        async (direction: 'prev' | 'next') => {
            if (currentHouseGoodsItemIndex === -1) return;

            const target = currentHouseGoodsItems[currentHouseGoodsItemIndex + (direction === 'next' ? 1 : -1)];
            if (!target?.id) return;

            setIsNavigating(true);
            try {
                const productSaved = await saveOpenProduct();
                if (!productSaved) return;

                const url = buildProductUrl(target.id);
                if (url) navigate(url, { replace: true });
            } finally {
                setIsNavigating(false);
            }
        },
        [buildProductUrl, currentHouseGoodsItemIndex, currentHouseGoodsItems, navigate, saveOpenProduct]
    );

    // The consignment form steps between house consignments, everything else between goods items.
    const itemNavigation = useMemo(() => {
        if (consignmentId && productId)
            return {
                disabledPrev: currentHouseGoodsItemIndex <= 0,
                disabledNext:
                    currentHouseGoodsItemIndex < 0 || currentHouseGoodsItemIndex >= currentHouseGoodsItems.length - 1,
                onPrev: () => handleNavigateToHouseGoodsItem('prev'),
                onNext: () => handleNavigateToHouseGoodsItem('next'),
            };

        if (consignmentId && !productId)
            return {
                disabledPrev: currentHouseConsignmentIndex <= 0,
                disabledNext:
                    currentHouseConsignmentIndex < 0 || currentHouseConsignmentIndex >= houseConsignmentsCount - 1,
                onPrev: () => handleNavigateToHouseConsignment('prev'),
                onNext: () => handleNavigateToHouseConsignment('next'),
            };

        return {
            disabledPrev: globalIndex <= 0,
            disabledNext: globalIndex < 0 || globalIndex >= products.total - 1,
            onPrev: () => handleNavigateToProduct('prev'),
            onNext: () => handleNavigateToProduct('next'),
        };
    }, [
        consignmentId,
        productId,
        currentHouseConsignmentIndex,
        houseConsignmentsCount,
        handleNavigateToHouseConsignment,
        currentHouseGoodsItemIndex,
        currentHouseGoodsItems.length,
        handleNavigateToHouseGoodsItem,
        globalIndex,
        products.total,
        handleNavigateToProduct,
    ]);

    const productNavigationType: ProductsNavigationTypes = useMemo(() => {
        if (selectedFormTab === FormTabs.MASTER_ITEMS) return 'master-items';
        if (selectedFormTab === FormTabs.HOUSE_CONSIGNMENTS) {
            return productId ? 'house-consignments-product' : 'house-consignments';
        }
        return 'items';
    }, [productId, selectedFormTab]);

    const isMasterItemsListRoute = !!useMatch({
        path: `${basePath}/master-items`,
        end: true,
    });

    const isHouseConsignmentsListRoute = !!useMatch({
        path: `${basePath}/house-consignments`,
        end: true,
    });

    const showBox44 =
        !isMasterItemsListRoute &&
        (!!declaration?.irelandH7ImportDeclaration || !!declaration?.irelandImportDeclaration);
    // ||
    // Note: Temporarily disable the Box44 for ENS declarations until we clarify the requirements
    // !!declaration?.entrySummaryDeclaration ||
    // !!declaration?.ics2ENSDeclaration

    const showItemNavigation = productId || consignmentId;

    // On an item the Box 44 modal sits next to the item navigation, so it is the one an error opens.
    useOpenBox44OnRequest(showBox44 && !!showItemNavigation, () => setShowBox44Modal(true));

    // Update the total items whenever we fetch products without a query params
    // This way we don't show the filtered number of items in the tab
    useEffect(() => {
        if (!activeProductParams?.query && !isProductLoading) {
            setUnfilteredTotal(numItems);
        }
    }, [numItems, activeProductParams, isProductLoading]);

    return (
        <>
            <Row justify="start" align="middle" style={{ gap: '2rem', marginBottom: '0.8rem' }}>
                <FormTabsWrapper error={hasAnyTabError}>
                    {visibleTabs.map((tab, index) => {
                        const isSelected = selectedFormTab === tab;
                        const label = tabConfig[tab].label;
                        const error = tabConfig[tab]?.error;
                        // The products list holds the master level items for ICS2, so both tabs count
                        // off the same total. House consignments are counted by the declaration.
                        let numItemsIcon;
                        if (tab === FormTabs.PRODUCTS || tab === FormTabs.MASTER_ITEMS)
                            numItemsIcon = `(${unfilteredTotal})`;
                        if (tab === FormTabs.HOUSE_CONSIGNMENTS) numItemsIcon = `(${houseConsignmentsCount})`;
                        const content = (
                            <>
                                {label} {numItemsIcon} {error && <InvalidIcon />}
                            </>
                        );

                        return isSelected ? (
                            <BtMasterGoodsSelected key={tab} left={index === 0} error={error}>
                                {content}
                            </BtMasterGoodsSelected>
                        ) : (
                            <BtMasterGoodsNotSelected
                                key={tab}
                                left={index === 0}
                                error={error}
                                onClick={() => saveChangesAndMove(tab)}
                            >
                                {content}
                            </BtMasterGoodsNotSelected>
                        );
                    })}
                </FormTabsWrapper>

                <DeclarationInformation
                    viewOnly={viewOnly}
                    amendment={amendment}
                    handleViewAllProductsBtn={() => setShowProducts?.(true)}
                    handleAddNewProductBtn={handleCreateDeclarationProduct}
                    handleAddMasterItemBtn={handleCreateMasterItemProduct}
                    handleAddHouseConsignmentBtn={handleCreateHouseConsignment}
                    handleBackToMasterItemsBtn={handleBackToMasterItems}
                    handleBackToHouseConsignmentsBtn={handleBackToHouseConsignments}
                    handleAddHouseConsignmentProductBtn={
                        consignmentId
                            ? async () => {
                                  await handleCreateHouseConsignmentProduct?.(consignmentId);
                              }
                            : undefined
                    }
                    refetchProducts={refetchProducts}
                    productsCount={products.total}
                    visibleInfo={{
                        toMasterItems: selectedFormTab === FormTabs.MASTER_ITEMS && !!productId,
                        toHouseConsignments: selectedFormTab === FormTabs.HOUSE_CONSIGNMENTS && !!consignmentId,
                        viewAllProducts: selectedFormTab === FormTabs.PRODUCTS,
                        createProduct: selectedFormTab === FormTabs.PRODUCTS,
                        bulkUploadItems: showBulkUploadItems,
                        addMasterItem: selectedFormTab === FormTabs.MASTER_ITEMS,
                        addHouseConsignment: selectedFormTab === FormTabs.HOUSE_CONSIGNMENTS && !consignmentId,
                        addHouseConsignmentProduct:
                            selectedFormTab === FormTabs.HOUSE_CONSIGNMENTS && !isHouseConsignmentsListRoute,
                        gmrId: isGvms && !!declaration?.gvmsDeclaration,
                        status: (isGvms && !!declaration?.status) || !!declaration?.preBoardingNotification?.status,
                        record:
                            !!declaration?.gvmsDeclaration?.messageType ||
                            !!declaration?.preBoardingNotification?.pbnId,
                        box44: showBox44 && !showItemNavigation,
                    }}
                />
            </Row>

            {showItemNavigation && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <ProductsNavigation
                            type={productNavigationType}
                            {...itemNavigation}
                            productIndex={
                                consignmentId
                                    ? currentHouseGoodsItemIndex >= 0
                                        ? currentHouseGoodsItemIndex + 1
                                        : undefined
                                    : productNumber
                            }
                            typeIndex={currentHouseConsignmentIndex >= 0 ? currentHouseConsignmentIndex + 1 : undefined}
                            loading={isNavigating || isProductLoading}
                        />
                    </div>

                    {/* When we have the navigation, display he box44 here, otherwise, display it in DeclarationInformation */}
                    {showBox44 && (
                        <InfoWrapper>
                            <Box44.Button
                                onClick={() => setShowBox44Modal((prev) => !prev)}
                                itemNumber={globalIndex >= 0 ? globalIndex + 1 : undefined}
                            />
                            <Box44.Modal
                                viewOnly={viewOnly && !amendment}
                                open={showBox44Modal && !isProductLoading}
                                itemNumber={productNumber}
                                onCancel={() => setShowBox44Modal(false)}
                            />
                        </InfoWrapper>
                    )}
                </div>
            )}
        </>
    );
};
export default FormNavigation;
